import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, useAuth } from '@halo/web-lib';
import type { ConnectionData } from '../../components/integrations';
import { getConnectionPlatformId } from '../../constants/report-platforms';

interface UseReportConnectionsResult {
  isLoading: boolean;
  /** True only when the platform has a connection that is active and fully set up. */
  isConnected: (reportPlatformId: string) => boolean;
  connectionFor: (reportPlatformId: string) => ConnectionData | null;
}

// Stable fallback: a fresh `[]` default on every render (while loading or after an
// error) would rebuild connectionMap and every callback derived from it.
const NO_CONNECTIONS: ConnectionData[] = [];

export function useReportConnections(): UseReportConnectionsResult {
  const { storeId } = useAuth();

  const { data: connections = NO_CONNECTIONS, isLoading } = useQuery({
    // Same key as IntegrationsPage so connect/disconnect there invalidates here.
    queryKey: ['connections', storeId],
    queryFn: async ({ signal }) => {
      const res = await api.get<ConnectionData[]>('/api/v1/oauth/connections', { signal });
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Failed to load connections');
      }
      return res.data;
    },
  });

  const connectionMap = useMemo(() => {
    const map = new Map<string, ConnectionData>();
    for (const c of connections) {
      map.set(c.platform, c);
    }
    return map;
  }, [connections]);

  // Stable callbacks so consumers can safely list them in memo/effect deps.
  const connectionFor = useCallback(
    (reportPlatformId: string): ConnectionData | null =>
      connectionMap.get(getConnectionPlatformId(reportPlatformId)) ?? null,
    [connectionMap],
  );

  const isConnected = useCallback(
    (reportPlatformId: string): boolean => {
      const c = connectionFor(reportPlatformId);
      return !!c && c.isActive && !c.needsSetup;
    },
    [connectionFor],
  );

  return { isLoading, isConnected, connectionFor };
}
